import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useCreatePaiementFormateur,
  useUpdatePaiementFormateur,
  type PaiementFormateurRow,
  type PaiementFormateurStatut,
} from '@/hooks/queries/usePaiementsFormateurs';
import { useTiers, useUpsertTier } from '@/hooks/queries/useTiers';
import { useFormations } from '@/hooks/queries/useFormation';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  activiteId: string | null;
  /** Si fourni : mode édition (préremplit + UPDATE). Sinon : mode création (INSERT). */
  paiement?: PaiementFormateurRow | null;
}

const STATUT_OPTIONS: { code: PaiementFormateurStatut; label: string }[] = [
  { code: 'a_payer', label: 'Non payé' },
  { code: 'paye', label: 'Payé' },
  { code: 'non_retribue', label: 'Non rétribué' },
];

function formatNumberInput(n: number | ''): string {
  if (n === '' || n === 0) return '';
  return n.toLocaleString('fr-DZ', { maximumFractionDigits: 0 });
}

function parseNumberInput(s: string): number {
  const cleaned = s.replace(/[^\d-]/g, '');
  if (!cleaned || cleaned === '-') return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

export function CreatePaiementFormateurDialog({
  open,
  onOpenChange,
  entrepriseId,
  activiteId,
  paiement,
}: Props): JSX.Element {
  const isEdit = !!paiement;
  const create = useCreatePaiementFormateur();
  const update = useUpdatePaiementFormateur();
  const upsertTier = useUpsertTier();
  const { data: tiers = [] } = useTiers({
    entrepriseId,
    type: 'formateur',
    search: '',
  });
  const { data: formations = [] } = useFormations({ entrepriseId });

  const [annee, setAnnee] = useState<number>(new Date().getFullYear());
  const [periode, setPeriode] = useState('');
  const [formateurNom, setFormateurNom] = useState('');
  const [formateurTierId, setFormateurTierId] = useState<string>('');
  const [coordonnees, setCoordonnees] = useState('');
  const [formationTitre, setFormationTitre] = useState('');
  const [formationId, setFormationId] = useState<string>('');
  const [dateDeroulement, setDateDeroulement] = useState('');
  const [montant, setMontant] = useState<number | ''>('');
  const [montantDisplay, setMontantDisplay] = useState('');
  const [statut, setStatut] = useState<PaiementFormateurStatut>('a_payer');
  const [remarques, setRemarques] = useState('');

  // Reset / préremplit selon le mode
  useEffect(() => {
    if (!open) return;
    if (paiement) {
      setAnnee(paiement.annee);
      setPeriode(paiement.periode ?? '');
      setFormateurNom(paiement.formateur_nom);
      setFormateurTierId(paiement.formateur_tier_id ?? '');
      setCoordonnees(paiement.formateur_coordonnees ?? '');
      setFormationTitre(paiement.formation_titre ?? '');
      setFormationId(paiement.formation_id ?? '');
      setDateDeroulement(paiement.date_deroulement ?? '');
      setMontant(paiement.montant);
      setMontantDisplay(formatNumberInput(paiement.montant));
      setStatut(paiement.statut);
      setRemarques(paiement.remarques ?? '');
    } else {
      setAnnee(new Date().getFullYear());
      setPeriode('');
      setFormateurNom('');
      setFormateurTierId('');
      setCoordonnees('');
      setFormationTitre('');
      setFormationId('');
      setDateDeroulement('');
      setMontant('');
      setMontantDisplay('');
      setStatut('a_payer');
      setRemarques('');
    }
  }, [open, paiement]);

  // Auto-resolve tier dès qu'on tape un nom existant
  useEffect(() => {
    const match = tiers.find(
      (t) => t.raison_sociale.trim().toLowerCase() === formateurNom.trim().toLowerCase(),
    );
    if (match) {
      setFormateurTierId(match.id);
      if (!coordonnees && match.rib) setCoordonnees(match.rib);
    } else if (formateurTierId && match === undefined) {
      // Si user a effacé le nom, on relâche le link tier
      setFormateurTierId('');
    }
  }, [formateurNom, tiers, coordonnees, formateurTierId]);

  // Idem pour formation
  useEffect(() => {
    const match = formations.find(
      (f) => f.titre.trim().toLowerCase() === formationTitre.trim().toLowerCase(),
    );
    if (match) setFormationId(match.id);
    else if (formationId) setFormationId('');
  }, [formationTitre, formations, formationId]);

  const formateursList = useMemo(
    () => tiers.slice().sort((a, b) => a.raison_sociale.localeCompare(b.raison_sociale)),
    [tiers],
  );
  const formationsList = useMemo(
    () => formations.slice().sort((a, b) => a.titre.localeCompare(b.titre)),
    [formations],
  );

  const handleMontantChange = (s: string): void => {
    const n = parseNumberInput(s);
    setMontant(n === 0 ? '' : n);
    setMontantDisplay(s.replace(/[^\d ,.-]/g, ''));
  };
  const handleMontantBlur = (): void => {
    if (montant === '') return;
    setMontantDisplay(formatNumberInput(montant));
  };

  const submit = async (): Promise<void> => {
    if (!formateurNom.trim()) {
      toast.error('Nom du formateur obligatoire');
      return;
    }
    if (!entrepriseId) {
      toast.error('Entreprise non sélectionnée');
      return;
    }
    const m = typeof montant === 'number' ? montant : 0;
    if (m <= 0) {
      toast.error('Montant > 0 obligatoire');
      return;
    }

    try {
      // 1. Find or create formateur tier
      let resolvedTierId = formateurTierId;
      if (!resolvedTierId && formateurNom.trim()) {
        const created = await upsertTier.mutateAsync({
          entreprise_id: entrepriseId,
          type: 'formateur',
          raison_sociale: formateurNom.trim(),
          activite_principale_id: activiteId,
        });
        resolvedTierId = created.id;
        toast.success(`Nouveau formateur créé : ${formateurNom.trim()}`);
      }

      // 2. Find or create formation
      let resolvedFormationId = formationId;
      if (!resolvedFormationId && formationTitre.trim()) {
        const code = `IMP-${Math.floor(Math.random() * 9000 + 1000)}`;
        const { data: createdF, error: errF } = await supabase
          .from('formations')
          .insert({
            entreprise_id: entrepriseId,
            code,
            titre: formationTitre.trim(),
          } as never)
          .select('id')
          .single();
        if (errF) {
          // eslint-disable-next-line no-console
          console.warn('Formation creation skipped:', errF.message);
        } else {
          resolvedFormationId = (createdF as { id: string } | null)?.id ?? '';
          if (resolvedFormationId) {
            toast.success(`Nouvelle formation créée : ${formationTitre.trim()}`);
          }
        }
      }

      // 3. INSERT or UPDATE paiement
      if (isEdit && paiement) {
        // Edit mode : on n'autorise PAS le toggle statut payé/non-payé ici.
        // Le statut change uniquement via "Marquer payé" / "Annuler paiement" du drawer.
        await update.mutateAsync({
          id: paiement.id,
          annee,
          periode: periode.trim() || null,
          formateur_tier_id: resolvedTierId || null,
          formateur_nom: formateurNom.trim(),
          formateur_coordonnees: coordonnees.trim() || null,
          formation_id: resolvedFormationId || null,
          formation_titre: formationTitre.trim() || null,
          date_deroulement: dateDeroulement || null,
          montant: m,
          remarques: remarques.trim() || null,
        });
        toast.success('Paiement modifié');
      } else {
        await create.mutateAsync({
          entreprise_id: entrepriseId,
          activite_id: activiteId,
          annee,
          periode: periode.trim() || null,
          formateur_tier_id: resolvedTierId || null,
          formateur_nom: formateurNom.trim(),
          formateur_coordonnees: coordonnees.trim() || null,
          formation_id: resolvedFormationId || null,
          formation_titre: formationTitre.trim() || null,
          date_deroulement: dateDeroulement || null,
          montant: m,
          statut,
          remarques: remarques.trim() || null,
        });
        toast.success(
          statut === 'paye'
            ? 'Paiement créé · pensez à valider via "Marquer payé" pour générer l\'opération Caisse'
            : 'Paiement créé',
        );
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  const isSubmitting = create.isPending || update.isPending || upsertTier.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col p-0">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Modifier le paiement' : 'Nouveau paiement formateur'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Année" required>
              <input
                type="number"
                value={annee}
                onChange={(e) => setAnnee(Number(e.target.value))}
                className="input"
                min={2000}
                max={2100}
                required
              />
            </Field>
            <Field label="Période" hint="Ex. Mai 2026, Q1 2025">
              <input
                value={periode}
                onChange={(e) => setPeriode(e.target.value)}
                className="input"
                placeholder="Mai 2026"
              />
            </Field>
            <Field label="Date déroulement">
              <input
                type="date"
                value={dateDeroulement}
                onChange={(e) => setDateDeroulement(e.target.value)}
                className="input"
              />
            </Field>
          </div>

          <Field
            label="Formateur"
            required
            hint={
              formateurTierId
                ? '✓ Formateur existant lié'
                : formateurNom.trim()
                  ? '⚠ Nouveau formateur — sera créé automatiquement à la sauvegarde'
                  : 'Tapez ou choisissez dans la liste'
            }
            warn={!formateurTierId && !!formateurNom.trim()}
          >
            <input
              list="formateurs-list"
              value={formateurNom}
              onChange={(e) => setFormateurNom(e.target.value)}
              className="input"
              placeholder="Nom complet du formateur"
              required
            />
            <datalist id="formateurs-list">
              {formateursList.map((t) => (
                <option key={t.id} value={t.raison_sociale} />
              ))}
            </datalist>
          </Field>

          <Field label="Coordonnées (RIB / IBAN)">
            <input
              value={coordonnees}
              onChange={(e) => setCoordonnees(e.target.value)}
              className="input font-mono text-xs"
              placeholder="BNA — DZ58 0000 0000 0000 0000 0000 12"
            />
          </Field>

          <Field
            label="Formation"
            hint={
              formationId
                ? '✓ Formation existante liée'
                : formationTitre.trim()
                  ? '⚠ Nouvelle formation — sera créée automatiquement à la sauvegarde'
                  : 'Optionnel — laisser vide si pas de session'
            }
            warn={!formationId && !!formationTitre.trim()}
          >
            <input
              list="formations-list"
              value={formationTitre}
              onChange={(e) => setFormationTitre(e.target.value)}
              className="input"
              placeholder="Titre de la formation"
            />
            <datalist id="formations-list">
              {formationsList.map((f) => (
                <option key={f.id} value={f.titre} />
              ))}
            </datalist>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Montant (DA)" required>
              <input
                value={montantDisplay}
                onChange={(e) => handleMontantChange(e.target.value)}
                onBlur={handleMontantBlur}
                className="input tabular-nums"
                placeholder="180 000"
                inputMode="numeric"
                required
              />
            </Field>
            <Field
              label="Statut"
              hint={
                isEdit
                  ? 'Statut non modifiable ici — utilisez les boutons dédiés du drawer'
                  : 'Statut initial à la création'
              }
            >
              <div
                className={cn('grid grid-cols-3 gap-1', isEdit && 'pointer-events-none opacity-60')}
              >
                {STATUT_OPTIONS.map((s) => (
                  <button
                    key={s.code}
                    type="button"
                    onClick={() => !isEdit && setStatut(s.code)}
                    className={cn(
                      'rounded-lg border-2 px-2 py-1.5 text-[11px] font-semibold transition-all',
                      statut === s.code
                        ? 'border-violet-500 bg-violet-50 text-violet-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          <Field label="Remarques">
            <textarea
              value={remarques}
              onChange={(e) => setRemarques(e.target.value)}
              className="input min-h-[60px]"
              rows={2}
              placeholder="Net après IRG 10%…"
            />
          </Field>

          {!isEdit && statut === 'paye' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <strong>Note :</strong> En mode &quot;Payé&quot;, la création n&apos;émet PAS encore
              l&apos;opération Caisse. Cliquez sur <em>Marquer payé</em> dans la liste après
              création pour choisir caisse/compte et générer l&apos;opération SORTIE.
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={isSubmitting}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-violet-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {isEdit ? 'Enregistrer' : 'Créer le paiement'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  required,
  warn,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  warn?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
      {hint && (
        <span
          className={cn(
            'mt-0.5 block',
            warn ? 'text-xs font-medium text-amber-600' : 'text-[10px] text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
